import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  IconButton
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Description as DocumentIcon,
  Psychology as AIIcon,
  Email as EmailIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Approval as ApprovalIcon,
  Notifications as NotificationIcon,
  Info as InfoIcon,
  Upload as UploadIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { formatArabicCurrency } from '../utils/arabicNumbers';

const AgentWorkflow = () => {
  const { t } = useTranslation();
  const [isRunning, setIsRunning] = useState(false);
  const [workflowResult, setWorkflowResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState('');
  const [progressInterval, setProgressInterval] = useState(null);
  const [promotionPolicy, setPromotionPolicy] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${apiUrl}/upload-policy`, {
          method: 'POST',
          body: formData
        });
        
        const result = await response.json();
        
        if (response.ok) {
          setPromotionPolicy(`تم رفع ${result.filename} إلى قاعدة المعرفة`);
          setUploadedFile(result.filename);
          setError('');
        } else {
          setError(result.detail || 'فشل في رفع الملف');
          setUploadedFile(null);
        }
      } catch (err) {
        setError('فشل في رفع الملف. تأكد من اتصالك بالإنترنت.');
        setUploadedFile(null);
      }
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const workflowSteps = [
    { name: 'رفع سياسة الترقيات', icon: <DocumentIcon />, description: 'تحميل وتحديد سياسة الترقيات المعتمدة' },
    { name: t('agent.workflowSteps.readApplications'), icon: <DocumentIcon />, description: t('agent.workflowSteps.readApplicationsDesc') },
    { name: t('agent.workflowSteps.aiAnalysis'), icon: <AIIcon />, description: t('agent.workflowSteps.aiAnalysisDesc') },
    { name: t('agent.workflowSteps.hrNotification'), icon: <EmailIcon />, description: t('agent.workflowSteps.hrNotificationDesc') },
    { name: t('agent.workflowSteps.financialAnalysis'), icon: <MoneyIcon />, description: t('agent.workflowSteps.financialAnalysisDesc') },
    { name: t('agent.workflowSteps.meetingScheduled'), icon: <EventIcon />, description: t('agent.workflowSteps.meetingScheduledDesc') },
    { name: t('agent.workflowSteps.finalApproval'), icon: <ApprovalIcon />, description: t('agent.workflowSteps.finalApprovalDesc') },
    { name: t('agent.workflowSteps.employeeNotifications'), icon: <NotificationIcon />, description: t('agent.workflowSteps.employeeNotificationsDesc') }
  ];

  const runWorkflow = async () => {
    setIsRunning(true);
    setWorkflowResult(null);
    setCurrentStep(0);
    setError('');

    // Clear any existing interval
    if (progressInterval) {
      clearInterval(progressInterval);
    }

    // Simulate real-time progress updates
    const simulateProgress = () => {
      const totalSteps = workflowSteps.length;
      let step = 0;
      
      const interval = setInterval(() => {
        if (step < totalSteps - 1) {
          step++;
          setCurrentStep(step);
        }
      }, 2000); // Update every 2 seconds
      
      setProgressInterval(interval);
      return interval;
    };

    const interval = simulateProgress();

    try {
      const response = await fetch(`${apiUrl}/agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          use_uploaded_data: true,
          promotion_policy: promotionPolicy
        }),
      });

      const result = await response.json();
      
      // Clear the progress simulation
      clearInterval(interval);
      setProgressInterval(null);
      
      if (response.ok) {
        // Set final step as completed
        setCurrentStep(workflowSteps.length - 1);
        setWorkflowResult(result);
      } else {
        setError(result.detail || t('agent.workflowFailed'));
      }
    } catch (error) {
      console.error('Workflow error:', error);
      clearInterval(interval);
      setProgressInterval(null);
      setError(t('agent.error'));
    } finally {
      setIsRunning(false);
    }
  };

  const getStepStatus = (index) => {
    if (workflowResult?.workflow_steps) {
      const resultStep = workflowResult.workflow_steps.find(s => s.step === index + 1);
      return resultStep ? resultStep.status : 'pending';
    } else if (isRunning && index <= currentStep) {
      return index === currentStep ? 'running' : 'completed';
    }
    return 'pending';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PlayIcon sx={{ fontSize: 40, color: 'white' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('agent.title')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: 'white', opacity: 1 }}>
              {t('agent.description')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Promotion Policy Upload */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon color="primary" />
            رفع سياسة الترقيات
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', p: 3, border: '2px dashed', borderColor: 'grey.300', borderRadius: 1 }}>
            <input
              accept=".txt,.pdf"
              style={{ display: 'none' }}
              id="policy-file-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="policy-file-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadIcon />}
                size="large"
              >
                اختيار ملف السياسة
              </Button>
            </label>
            {uploadedFile && (
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setPromotionPolicy('');
                  setUploadedFile(null);
                }}
                size="large"
              >
                مسح
              </Button>
            )}
          </Box>
          {uploadedFile ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              ✓ تم رفع الملف إلى قاعدة المعرفة: {uploadedFile}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              لم يتم رفع ملف - سيتم استخدام قاعدة المعرفة الافتراضية
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Workflow Control */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={runWorkflow}
              disabled={isRunning}
              startIcon={isRunning ? <CircularProgress size={20} /> : <PlayIcon />}
              color="primary"
              sx={{ minWidth: 200 }}
            >
              {isRunning ? t('agent.workflowRunning') : t('agent.startWorkflow')}
            </Button>
            {workflowResult && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setWorkflowResult(null);
                  setError('');
                }}
              >
                إعادة تعيين
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Workflow Steps */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                خطوات سير العمل
              </Typography>
              <Stepper orientation="vertical">
                {workflowSteps.map((step, index) => {
                  const status = getStepStatus(index);
                  const isActive = status === 'running';
                  const isCompleted = status === 'completed';

                  return (
                    <Step key={index} active={isActive} completed={isCompleted}>
                      <StepLabel
                        icon={
                          status === 'running' ? (
                            <CircularProgress size={24} />
                          ) : status === 'completed' ? (
                            <SuccessIcon color="success" />
                          ) : status === 'error' ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <PendingIcon color="disabled" />
                          )
                        }
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {step.icon}
                          <Typography variant="subtitle1">
                            {step.name}
                          </Typography>
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Workflow Info */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoIcon color="primary" />
                <Typography variant="h6">
                  حول سير العمل
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                يقوم وكيل الذكاء الاصطناعي بمعالجة طلبات الترقية تلقائياً من خلال المراحل التالية:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AIIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="تحليل البيانات" 
                    secondary="يقوم الذكاء الاصطناعي بتقييم كل طلب وفقاً لمعايير الترقية" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MoneyIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="التخطيط المالي" 
                    secondary="حساب الأثر المالي للترقيات المعتمدة" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="الإشعارات التلقائية" 
                    secondary="إرسال التحديثات لمديري الموارد البشرية والموظفين" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EventIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="تنسيق الاجتماعات" 
                    secondary="جدولة اجتماعات الموافقة اللازمة" 
                  />
                </ListItem>
              </List>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                يستغرق سير العمل عادة من 2-5 دقائق حسب عدد الطلبات.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Workflow Results */}
        {workflowResult && workflowResult.status === 'success' && (
          <Grid size={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  نتائج سير العمل
                </Typography>

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined" sx={{ backgroundColor: 'success.main', color: 'white' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <SuccessIcon sx={{ fontSize: 40, mb: 1, color: 'white' }} />
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {workflowResult.approved}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          معتمد
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined" sx={{ backgroundColor: 'error.main', color: 'white' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <ErrorIcon sx={{ fontSize: 40, mb: 1, color: 'white' }} />
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {workflowResult.rejected}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          مرفوض
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined" sx={{ backgroundColor: 'info.light', color: 'info.contrastText' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <DocumentIcon sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4">
                          {workflowResult.applications_processed}
                        </Typography>
                        <Typography variant="body2">
                          المجموع المعالج
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Financial Impact */}
                {workflowResult.financial_impact && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoneyIcon color="primary" />
                      الأثر المالي
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Chip
                          label={`الراتب الشهري الحالي: ${formatArabicCurrency(workflowResult.financial_impact.current_monthly_total)} ريال`}
                          variant="outlined"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Chip
                          label={`الراتب الشهري الجديد: ${formatArabicCurrency(workflowResult.financial_impact.new_monthly_total)} ريال`}
                          variant="outlined"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Chip
                          label={`الزيادة الشهرية: ${formatArabicCurrency(workflowResult.financial_impact.monthly_increase)} ريال`}
                          color="primary"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Chip
                          label={`الزيادة السنوية: ${formatArabicCurrency(workflowResult.financial_impact.annual_increase)} ريال`}
                          color="secondary"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Policy Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocumentIcon color="primary" />
                    سياسة الترقية المستخدمة
                  </Typography>
                  <Alert 
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ mb: 2 }}
                  >
                    {workflowResult.policy_used === 'uploaded' ? 
                      'تم استخدام سياسة مخصصة مرفوعة' : 
                      'تم استخدام السياسة الافتراضية'
                    }
                  </Alert>
                  {workflowResult.policy_preview && (
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      backgroundColor: 'grey.100', 
                      p: 2, 
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      maxHeight: 150,
                      overflow: 'auto'
                    }}>
                      {workflowResult.policy_preview}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Notifications Status */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" />
                    الإشعارات
                  </Typography>
                  <Alert 
                    severity={workflowResult.notifications_sent ? 'success' : 'error'}
                    icon={workflowResult.notifications_sent ? <SuccessIcon /> : <ErrorIcon />}
                  >
                    {workflowResult.notifications_sent ? 
                      'تم إرسال جميع الإشعارات بنجاح' : 
                      'فشل في إرسال بعض الإشعارات'
                    }
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AgentWorkflow;